<template>
  <div class="plan-container plan-container__select">
    <p class="title">Select a QHA to quality assure</p>
    <div class="search-bar field">
      <span class="icon input-icon">
        <i class="fas fa-search"></i>
      </span>
      <input
        type="text"
        class="input has-icon"
        placeholder="Search by System ID"
        v-model="searchTerm"
      />
    </div>
    <div class="plan-list plan-list__home">
      <div class="table-wrapper table-wrapper__live" v-show="ongoing">
        <p class="list-description">Audits in progress:</p>
        <table class="table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Service</th>
              <th>Health Professional</th>
              <th>Key Stage</th>
              <th>Primary Need</th>
              <th>Setting Name</th>
              <th>Report Date</th>
              <th>Deadline</th>
              <th>Next Theme</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="assignment in ongoing"
              :key="assignment._id"
              @click="selectAssignment(assignment)"
              :class="{ selected: currentAssignmentId === assignment._id }"
            >
              <td>
                {{ assignment.planId.internalId || assignment.planId.ehcpId }}
              </td>
              <td>{{ assignment.planId.service }}</td>
              <td>{{ assignment.planId.healthProfessional }}</td>
              <td>{{ assignment.planId.stage }}</td>
              <td>{{ assignment.planId.need }}</td>
              <td>{{ assignment.planId.settingName }}</td>
              <td>{{ assignment.planId.reportDate | formatDate }}</td>
              <td>{{ assignment.deadlineDate | formatDate }}</td>
              <td>{{ nextSectionForAudit(assignment._id) | toUpper }}</td>
            </tr>
          </tbody>
        </table>
        <hr class="list-divider" />
      </div>
      <div class="table-wrapper table-wrapper__new">
        <p class="list-description">Audits not yet started:</p>
        <table class="table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Service</th>
              <th>Health Professional</th>
              <th>Key Stage</th>
              <th>Primary Need</th>
              <th>Setting Name</th>
              <th>Report Date</th>
              <th>Deadline</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="assignment in filteredNewAssignments"
              :key="assignment._id"
              @click="selectAssignment(assignment)"
              :class="{ selected: currentAssignmentId === assignment._id }"
            >
              <td>
                {{ assignment.planId.internalId || assignment.planId.ehcpId }}
              </td>
              <td>{{ assignment.planId.service }}</td>
              <td>{{ assignment.planId.healthProfessional }}</td>
              <td>{{ assignment.planId.stage }}</td>
              <td>{{ assignment.planId.need }}</td>
              <td>{{ assignment.planId.settingName }}</td>
              <td>{{ assignment.planId.reportDate | formatDate }}</td>
              <td>{{ assignment.deadlineDate | formatDate }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="info-row info-row--right">
        <p>Your progress will save automatically.</p>
        <p class="link" :class="{ disabled: !hasSelectedPlan }">
          <a @click="confirmAudit()">Proceed to audit</a>
          <i class="fas fa-chevron-circle-right"></i>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import Fuse from 'fuse.js'

export default {
  name: 'SelectPlan',
  data() {
    return {
      searchTerm: ''
    }
  },
  computed: {
    ...mapGetters({
      user: 'user/user',
      audits: 'audit/audits',
      assignmentsForUser: 'assignment/forUserId',
      currentAssignment: 'assignment/currentAssignment',
      currentAudit: 'audit/currentAudit'
    }),
    assignments() {
      return this.assignmentsForUser(this.user._id).filter(a => !a.archived)
        .length
        ? this.assignmentsForUser(this.user._id).filter(a => !a.archived)
        : []
    },
    currentAssignmentId() {
      return this.currentAssignment ? this.currentAssignment._id : null
    },
    hasSelectedPlan() {
      return this.currentAssignmentId !== null
    },
    newAssignments() {
      return this.assignments.filter(a => !a.startedAt && !a.completedAt)
    },
    ongoing() {
      return this.assignments.filter(a => a.startedAt && !a.completedAt)
    },
    filteredOngoingAssignments() {
      const fuse = new Fuse(this.ongoing, {
        includeScore: true,
        keys: ['planId.internalId']
      })
      return this.searchTerm.length < 3
        ? this.newAssignments
        : fuse.search(this.searchTerm).map(({ item }) => item)
    },
    filteredNewAssignments() {
      const fuse = new Fuse(this.newAssignments, {
        includeScore: true,
        keys: ['planId.internalId']
      })
      return this.searchTerm.length < 3
        ? this.newAssignments
        : fuse.search(this.searchTerm).map(({ item }) => item)
    }
  },
  filters: {
    toUpper(val) {
      return val.toUpperCase()
    }
  },
  methods: {
    ...mapActions({
      setCurrentAssignment: 'assignment/setCurrentAssignment',
      fetchAssignments: 'assignment/fetchAssignments',
      getAudits: 'audit/getAudits',
      setCurrentAudit: 'audit/setCurrentAudit',
      resetAudit: 'audit/reset'
    }),
    selectAssignment(assignment) {
      this.resetAudit()
      this.setCurrentAssignment(assignment)
    },
    nextSectionForAudit(assignmentId) {
      const audit = this.audits.find(
        a => a.assignmentId && a.assignmentId._id === assignmentId
      )

      /* eslint-disable-next-line */
      const nextSection =  audit && audit.hasOwnProperty('nextSection')
        ? audit.nextSection
          ? audit.nextSection
          : 'summary'
        : 'a'
      // sections map
      const sectionsMap = {
        1: 'a',
        2: 'b',
        3: 'c',
        4: 'd',
        5: 'e',
        6: 'f',
        7: 'additional-components',
        8: 'next-steps'
      }
      if (nextSection.toLowerCase() === 'summary') {
        return nextSection
      }
      let numberThemes = ''
      for (const key in sectionsMap) {
        if (sectionsMap[key] === nextSection.toLowerCase()) {
          numberThemes = key
        }
      }
      return numberThemes
    },
    confirmAudit() {
      // Clear in case we had a previous audit loaded.
      this.resetAudit()

      // Set current audit based on selected assignment.
      if (this.currentAssignment._id) {
        this.setCurrentAudit(this.currentAssignment._id)
      }

      // Navigate.
      this.$router.push('/plans/details')
    }
  },
  async mounted() {
    this.getAudits()
    this.fetchAssignments()
  }
}
// TODO: force update on each screen
// TODO: number of audits / number of assignments
</script>

<style lang="scss" scoped>
@import '@/style/variables.scss';
@import '@/style/components.scss';
@import '@/style/sections.scss';
</style>
